import {useEffect, useMemo} from 'react';

import {AirtableFeedItem} from '../../models/airtable';
import {useStores} from '../../stores/use-stores';

import {ALL_SEGMENT_VALUE, useFeedDiscoverList} from './useFeedDiscoverList';

export type ResolveDiscoverArticleStatus = 'idle' | 'loading' | 'found' | 'not_found' | 'error';

export type UseResolveDiscoverArticleParams = {
  /** Article URL from the linking stub (`/discover/:slug`). */
  url: string | undefined;
  /** True only for `__pending` stubs — non-stubs skip resolution. */
  enabled: boolean;
};

export type UseResolveDiscoverArticleResult = {
  article: AirtableFeedItem | undefined;
  status: ResolveDiscoverArticleStatus;
};

/**
 * Resolves a cold-start Discover deep-link stub from the TanStack ALL-segment
 * infinite query (shared with Discover list / AppNavigationContainer prefetch).
 *
 * Walks pages via `fetchNextPage` until a case-insensitive `url` match or
 * exhaustion. Does not read or write `feedStore.discoverList` (empty after
 * DIS-1344; dual-write would reintroduce the DIS-1306 race).
 */
export const useResolveDiscoverArticle = ({
  url,
  enabled,
}: UseResolveDiscoverArticleParams): UseResolveDiscoverArticleResult => {
  const {shopSelectionStore} = useStores();
  const selectedStore = shopSelectionStore?.selectedStore;
  const storeNumber = selectedStore?.store_number;
  const storeState = selectedStore?.address?.administrative_area?.toUpperCase();

  const {data, isPending, isFetchingNextPage, hasNextPage, isError, fetchNextPage} =
    useFeedDiscoverList({
      storeNumber,
      storeState,
      segment: ALL_SEGMENT_VALUE,
    });

  const article = useMemo(() => {
    if (!enabled || !url) return undefined;
    const needle = url.toLowerCase();
    for (const page of data?.pages ?? []) {
      for (const item of page.discoverList) {
        if (item.url?.toLowerCase() === needle) return item;
      }
    }
    return undefined;
  }, [enabled, url, data?.pages]);

  useEffect(() => {
    if (!enabled || article || isError || isPending || !hasNextPage || isFetchingNextPage) {
      return;
    }
    void fetchNextPage();
  }, [enabled, article, isError, isPending, hasNextPage, isFetchingNextPage, fetchNextPage]);

  const status = useMemo((): ResolveDiscoverArticleStatus => {
    if (!enabled) return 'idle';
    if (article) return 'found';
    if (isError) return 'error';
    if (!storeNumber || !storeState || isPending || isFetchingNextPage || hasNextPage) {
      return 'loading';
    }
    return 'not_found';
  }, [
    enabled,
    article,
    isError,
    storeNumber,
    storeState,
    isPending,
    isFetchingNextPage,
    hasNextPage,
  ]);

  return {article, status};
};
